import { useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, ButtonVariants } from '@carboledger/ui';
import { ROUTES } from '@/app/routes';
import { EmptyState, LoadingOverlay } from '@/components/feedback';
import { useTranslations } from '@/hooks';
import { OrderForm } from '../components/OrderForm';
import { useGetOrderFormQuery, useUpdateOrderMutation } from '../api/ordersApi';
import { useOrderFormOptions } from '../hooks';

// Outbounds ▸ Orders ▸ Edit. Same form as Add.
export const EditOrderPage = () => {
  const t = useTranslations();
  const navigate = useNavigate();
  const { module, orderId } = useParams();

  // The prefill carries no cache tag, so freshness is asked for here rather than invalidated.
  const {
    data,
    isLoading: isLoadingOrder,
    isError,
  } = useGetOrderFormQuery({ id: orderId }, { refetchOnMountOrArgChange: true });
  const { options, sites, materials, getEquivalentUnit, isLoading } = useOrderFormOptions();
  const [updateOrder, { isLoading: isSubmitting }] = useUpdateOrderMutation();

  const goToOrders = useCallback(() => {
    navigate(ROUTES.orders(module));
  }, [navigate, module]);

  const handleSubmit = useCallback(
    async (values) => {
      const result = await updateOrder({
        module,
        id: orderId,
        values,
        equivalentUnit: getEquivalentUnit(values),
      });
      if (result.error) return;
      goToOrders();
    },
    [updateOrder, module, orderId, getEquivalentUnit, goToOrders],
  );

  // Wait for both: <Form> seeds initialValues once, on mount.
  if (isLoadingOrder || isLoading) {
    return <LoadingOverlay label={t('COMMON_LOADING')} />;
  }

  if (isError || !data) {
    return (
      <EmptyState
        title={t('ORDERS_LOAD_ERROR')}
        action={
          <Button variant={ButtonVariants.secondary} onClick={goToOrders}>
            {t('COMMON_BACK')}
          </Button>
        }
      />
    );
  }

  return (
    <OrderForm
      title={t('ORDERS_EDIT_TITLE')}
      submitLabel={t('COMMON_SAVE')}
      initialValues={data.values}
      options={options}
      sites={sites}
      materials={materials}
      onSubmit={handleSubmit}
      onClose={goToOrders}
      isSubmitting={isSubmitting}
      isEdit
      isLocked={data.isLocked}
      shippedByUnit={data.shippedByUnit}
      allocatedByUnit={data.allocatedByUnit}
    />
  );
};
